import { useEffect, useState } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { TrendingUp } from "lucide-react";

const REVENUE_COLOR = "hsl(199 89% 48%)"; // primary ocean-cyan (design tokens)
const ORDERS_COLOR = "hsl(38 92% 50%)"; // amber secondary accent (status-mismatch token)

const usd = (cents) =>
  (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  const revenue = payload.find((item) => item.dataKey === "revenue");
  const orders = payload.find((item) => item.dataKey === "orders");
  return (
    <div
      className="rounded-md border border-border bg-popover px-3 py-2 shadow-lg"
      data-testid="revenue-chart-tooltip"
    >
      <p className="font-display text-xs font-semibold mb-1">{label}</p>
      {revenue && (
        <p className="text-xs" style={{ color: REVENUE_COLOR }}>
          Revenue: {usd(revenue.value * 100)}
        </p>
      )}
      {orders && (
        <p className="text-xs" style={{ color: ORDERS_COLOR }}>
          Orders: {orders.value}
        </p>
      )}
    </div>
  );
};

export const RevenueChart = () => {
  const [months, setMonths] = useState(null);

  useEffect(() => {
    api
      .get("/admin/stats/monthly")
      .then(({ data }) =>
        setMonths(
          (data.months || []).map((month) => ({
            label: month.label,
            revenue: Math.round(month.revenueCents) / 100,
            orders: month.orders,
          }))
        )
      )
      .catch(() => setMonths([]));
  }, []);

  return (
    <Card className="bg-card border-border" data-testid="revenue-chart-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-display text-lg flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-primary" /> Revenue &amp; orders — last 12 months
        </CardTitle>
        <CardDescription>
          Bars show report revenue collected each month; the line tracks report requests submitted.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-2">
        {months === null ? (
          <Skeleton className="h-64 w-full" data-testid="revenue-chart-skeleton" />
        ) : (
          <div className="h-64 w-full" data-testid="revenue-chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={months} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                  axisLine={{ stroke: "hsl(var(--border))" }}
                  tickLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  yAxisId="revenue"
                  tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(value) => `$${value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value}`}
                  width={52}
                />
                <YAxis
                  yAxisId="orders"
                  orientation="right"
                  allowDecimals={false}
                  tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  width={36}
                />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: "hsl(var(--secondary))", opacity: 0.5 }} />
                <Bar
                  yAxisId="revenue"
                  dataKey="revenue"
                  name="Revenue"
                  fill={REVENUE_COLOR}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={36}
                  opacity={0.85}
                />
                <Line
                  yAxisId="orders"
                  type="monotone"
                  dataKey="orders"
                  name="Orders"
                  stroke={ORDERS_COLOR}
                  strokeWidth={2}
                  dot={{ r: 2.5, fill: ORDERS_COLOR, strokeWidth: 0 }}
                  activeDot={{ r: 4 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
        <div className="flex items-center gap-4 mt-3">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: REVENUE_COLOR }} />
            Revenue (USD)
          </span>
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="inline-block h-0.5 w-4 rounded" style={{ background: ORDERS_COLOR }} />
            Report orders
          </span>
        </div>
      </CardContent>
    </Card>
  );
};
